import { isIntuitTokenUrl } from '../../utils/intuitBankToken';
import { decimalCents } from './cashMoney';

export const CASH_DEPOSIT_AUTHORIZATION =
  'I authorize MedEffects to debit my bank account {amount} USD to fund my distributor dollar balance. Funds become available only after verified settlement. Refunded, returned, or disputed payments may pause spending until resolved.';
export const CASH_CARD_AUTHORIZATION = CASH_DEPOSIT_AUTHORIZATION.replace(
  'debit my bank account',
  'charge my card'
);
export const CASH_CARD_AUTHORIZATION_V2 =
  CASH_CARD_AUTHORIZATION +
  ' MedEffects covers the card processing fee; your card charge and distributor credit are both {amount} USD.';
export const CASH_CARD_AUTHORIZATION_V3 =
  'I authorize MedEffects to charge my card {amount} USD to add {amount} USD to my Virtual Tissue Bank Balance. Credit is added after QuickBooks confirms the successful charge. MedEffects covers the card processing fee. Failed, refunded, returned, or disputed payments may pause spending until resolved.';
export const CASH_CARD_FUNDING_POLICY_V1 = Object.freeze({
  version: 1,
  method: 'card',
  processingFeePayer: 'medeffects',
  customerFeeCents: 0,
  creditBasis: 'charged_principal',
});
export const CASH_CARD_FUNDING_POLICY = Object.freeze({
  ...CASH_CARD_FUNDING_POLICY_V1,
  version: 2,
  creditBasis: 'confirmed_charge_principal',
});
const matchesPolicy = (value, expected) =>
  Boolean(
    value &&
      !Array.isArray(value) &&
      Object.keys(value).length === Object.keys(expected).length &&
      Object.entries(expected).every(([key, field]) => value[key] === field)
  );
export const validCashCardFundingPolicy = (value) => matchesPolicy(value, CASH_CARD_FUNDING_POLICY);
const validHistoricalPolicy = (value) => matchesPolicy(value, CASH_CARD_FUNDING_POLICY_V1);
const samePolicy = (left, right) =>
  left === right ||
  ((validCashCardFundingPolicy(left) || validHistoricalPolicy(left)) &&
    (validCashCardFundingPolicy(right) || validHistoricalPolicy(right)) &&
    Object.keys(left).every((key) => left[key] === right[key]));
export const confirmedChargeDeposit = (value) =>
  cashDepositMethod(value) === 'card' &&
  value.authorizationVersion === 3 &&
  validCashCardFundingPolicy(value.fundingPolicy);
export const validCashFundingUser = (user, distributorId) =>
  user?.role === 'master_distributor' &&
  user.isActive !== false &&
  user.disabled !== true &&
  !user.distributorPreview &&
  !user.distributorPreviewExpiresAt &&
  /^[a-f\d]{24}$/i.test(distributorId || '') &&
  (user.distributor?._id || user.distributor) === distributorId;
export const CASH_DEPOSIT_METHODS = { wire: 'Wire', ach: 'ACH', card: 'Credit card' };
export const cashDepositMethod = (row) => (row?.method === undefined ? 'ach' : row.method);
export const cashFundingMethods = (row) =>
  row?.methods === undefined
    ? { wire: false, ach: row?.method === 'ach' && row.available, card: false }
    : row.methods;
export const CASH_DEPOSIT_STATES = {
  prepared: 'Ready for authorization',
  processing: 'Payment processing',
  pending: 'Awaiting verified settlement',
  unknown: 'Payment outcome unconfirmed',
  declined: 'Payment declined',
  failed: 'Payment needs review',
  returned: 'Payment returned',
  needs_review: 'Payment needs review',
  credited: 'Funds added',
};
const integer = (value, min = 0) => Number.isSafeInteger(value) && value >= min;
const stamp = (value) => typeof value === 'string' && Number.isFinite(Date.parse(value));
const id = (value) => typeof value === 'string' && /^[a-f\d]{24}$/i.test(value);
const requestKey = (value) => typeof value === 'string' && /^[A-Za-z0-9._:-]{16,100}$/.test(value);
export const cashDepositAuthorization = (amount, method = 'ach', version = 1) =>
  method === 'wire'
    ? null
    : (method === 'card'
        ? version === 3
          ? CASH_CARD_AUTHORIZATION_V3
          : version === 2
            ? CASH_CARD_AUTHORIZATION_V2
            : CASH_CARD_AUTHORIZATION
        : CASH_DEPOSIT_AUTHORIZATION
      ).replaceAll('{amount}', decimalCents(amount));

export function readCashDeposit(value) {
  const method = cashDepositMethod(value);
  if (
    !value ||
    !id(value._id) ||
    !requestKey(value.requestKey) ||
    !integer(value.amountCents, 1) ||
    value.currency !== 'USD' ||
    typeof method !== 'string' ||
    !Object.hasOwn(CASH_DEPOSIT_METHODS, method) ||
    !Object.hasOwn(CASH_DEPOSIT_STATES, value.status) ||
    typeof value.open !== 'boolean' ||
    !stamp(value.createdAt) ||
    ![1, 2, 3].includes(value.authorizationVersion) ||
    (value.authorizationVersion > 1
      ? method !== 'card' ||
        !(value.authorizationVersion === 3
          ? validCashCardFundingPolicy(value.fundingPolicy)
          : validHistoricalPolicy(value.fundingPolicy))
      : value.fundingPolicy !== undefined && value.fundingPolicy !== null) ||
    value.authorizationText !==
      cashDepositAuthorization(value.amountCents, method, value.authorizationVersion) ||
    (method === 'wire'
      ? !['prepared', 'credited'].includes(value.status) || value.attempt !== null
      : value.status === 'prepared'
        ? value.attempt !== null
        : !value.attempt || value.attempt.state !== value.status) ||
    (value.attempt?.checkedAt !== undefined && !stamp(value.attempt.checkedAt)) ||
    (value.status === 'credited' && (value.open || !stamp(value.creditedAt))) ||
    (!value.open &&
      value.status !== 'credited' &&
      (!['prepared', 'declined'].includes(value.status) || !stamp(value.closedAt)))
  )
    throw new Error('Cash deposit unavailable');
  return value;
}
export function sameCashDeposit(value, frozen) {
  return (
    cashDepositMethod(value) === cashDepositMethod(frozen) &&
    samePolicy(value?.fundingPolicy, frozen?.fundingPolicy) &&
    [
      '_id',
      'requestKey',
      'amountCents',
      'currency',
      'createdAt',
      'authorizationVersion',
      'authorizationText',
    ].every((key) => value?.[key] === frozen?.[key])
  );
}
export function validCashDepositOptions(value, distributorId) {
  const methods = cashFundingMethods(value);
  return (
    value?.schemaVersion === 1 &&
    id(value.distributorId) &&
    value.distributorId === distributorId &&
    typeof value.available === 'boolean' &&
    typeof value.method === 'string' &&
    Object.hasOwn(CASH_DEPOSIT_METHODS, value.method) &&
    (value.methods !== undefined || value.method === 'ach') &&
    methods &&
    !Array.isArray(methods) &&
    Object.keys(methods).length === 3 &&
    Object.keys(CASH_DEPOSIT_METHODS).every((method) => typeof methods[method] === 'boolean') &&
    (!value.available || Object.values(methods).some(Boolean)) &&
    value.currency === 'USD' &&
    integer(value.minAmountCents, 1) &&
    integer(value.maxAmountCents, value.minAmountCents) &&
    [1, 2, 3].includes(value.authorizationVersion) &&
    (value.tokenUrl === null
      ? !value.available || (!methods.ach && !methods.card)
      : isIntuitTokenUrl(value.tokenUrl))
  );
}
export function validCashPaymentOptions(value, deposit, distributorId) {
  return (
    validCashDepositOptions(value, distributorId) &&
    value.method === cashDepositMethod(deposit) &&
    value.method !== 'wire' &&
    (!value.available || cashFundingMethods(value)[value.method]) &&
    value.depositId === deposit._id &&
    value.amountCents === deposit.amountCents &&
    value.currency === deposit.currency &&
    value.authorizationVersion === deposit.authorizationVersion &&
    value.authorizationText === deposit.authorizationText &&
    deposit.amountCents >= value.minAmountCents &&
    deposit.amountCents <= value.maxAmountCents &&
    (!value.available ||
      (isIntuitTokenUrl(value.tokenUrl) &&
        deposit.open &&
        deposit.status === 'prepared' &&
        deposit.attempt === null))
  );
}
// New payments need the explicit merchant-covered policy and server readiness. Legacy
// options/receipts remain readable, but cannot authorize a new card charge.
export const validCashCardFundingOptions = (value, distributorId) =>
  validCashDepositOptions(value, distributorId) &&
  value.available === true &&
  value.method === 'card' &&
  value.methods?.card === true &&
  value.methods.ach === false &&
  value.methods.wire === false &&
  value.authorizationVersion === 3 &&
  validCashCardFundingPolicy(value.fundingPolicy) &&
  value.readiness?.ready === true &&
  value.readiness.code === 'CARD_FUNDING_READY' &&
  value.tokenUrl === 'https://api.intuit.com/quickbooks/v4/payments/tokens';
export const validCashCardPaymentOptions = (value, deposit, distributorId) =>
  validCashCardFundingOptions(value, distributorId) &&
  validCashPaymentOptions(value, deposit, distributorId) &&
  deposit.authorizationVersion === 3 &&
  validCashCardFundingPolicy(deposit.fundingPolicy) &&
  samePolicy(value.fundingPolicy, deposit.fundingPolicy);

export function readCashDepositHistory(value, page) {
  if (
    !Array.isArray(value?.items) ||
    !integer(value.total) ||
    value.total < value.items.length ||
    value.items.length > 10 ||
    value.page !== page ||
    value.limit !== 10
  )
    throw new Error('Cash funding history unavailable');
  value.items.forEach(readCashDeposit);
  return value;
}
export const depositStateLabel = (row) =>
  row.status === 'prepared' && !row.open
    ? 'Unsubmitted request closed'
    : row.status === 'pending' && confirmedChargeDeposit(row)
      ? 'Awaiting charge confirmation'
      : CASH_DEPOSIT_STATES[row.status];
